// The paywall on raisedbeef.com's tools (owner, 2026-09-15; plans 2026-10-09;
// built 2026-10-12 — mock https://claude.ai/artifact/TChK29A3Dskf6aF8jtqAF2).
//
// Wraps a page. Whether the page is gated comes from requiresAccount() in
// src/lib/siteAccess.ts; whether the person has the plan comes from the
// database. ONE PLAN since 2026-10-25 (src/lib/sitePlan.ts): there is no
// "upgrade" case any more, a page is open or it is not.
//   · signed out                → the membership page, naming the tool (one door)
//   · in the free 30 days       → the page, with a slim days-left line
//   · free month over, no plan  → the plan, monthly or yearly
//   · on the plan, comped, admin → the page
//
// ── WHAT IT NEVER DOES ───────────────────────────────────────────────────────
// · It never gates the free layer: the homepage and Boxed Beef are not wrapped.
// · It never shows a wall to a crawler, so Google keeps indexing and the
//   link-preview cards keep working.
// · It never shows a blank screen at day 31: the plans are right there, and the
//   homepage stays free.
// · It never flashes the sign-up card at someone who IS signed in: while the
//   session and account row load, the page renders nothing but the shell.
//
// The sign-up is deliberately two boxes and a button. Magic links were ruled out
// by the owner ("magic link will not work for cattle guys").

import { useSiteAccess } from "@/hooks/useSiteAccess";
import { isCrawler, toolName, trialLine } from "@/lib/siteAccess";
import { siteHomeHref } from "@/lib/siteHost";
import { gatePath, useInApp } from "@/rbapp/inApp";
import Paywall from "@/rbapp/Paywall";

import { TickerShell } from "../cattle-ticker/TickerShell";
import MemberPitch from "./MemberPitch";
import MembershipPage from "./MembershipPage";
import SiteBilling from "./SiteBilling";

// ⚠️ THE SHELL DOES NOT CARRY ITS OWN STYLES. TickerShell renders the navy
// masthead and footer, but the rules for them live in these two files, which
// every page that uses the shell imports for itself. The gate replaces the
// page, so without these the masthead renders as a giant logo and a run-on
// line of links — caught on the owner's phone, and the same shape as the black
// map in PR #2043: a stylesheet nothing imported.
import "../rbnext.css";
import "../cattle-ticker/tickerHome.css";
import "./rbAccount.css";

/** A declared crawler is resolved once, at module load: it cannot change
 *  mid-visit, and re-reading it per render would be noise. */
const CRAWLING = typeof navigator !== "undefined" && isCrawler(navigator.userAgent);

interface Props {
  children: React.ReactNode;
  /** What the card calls the page, when the address cannot say. */
  tool?: string;
  /** Drawn beside any wall (not beside the page): the lobby's wall display
   *  promotion, which the owner wants free for everyone (2026-10-12). */
  besideWall?: React.ReactNode;
}

export default function RequireAccount({ children, tool: toolOverride, besideWall }: Props) {
  // window.location, not useLocation: the barn top-prices pages boot from their
  // own entry with no router (entry-rbbarnlb.tsx).
  // In the app, the website page the screen stands for — the same plan on
  // both doors (owner, 2026-10-13). The hook below must not move under an
  // early return, so the context is read first.
  const inApp = useInApp();
  const here = typeof window !== "undefined" ? window.location.pathname : "/";
  const path = inApp ? gatePath(here, typeof window !== "undefined" ? window.location.search : "") : here;
  const { access } = useSiteAccess();
  const tool = toolOverride ?? toolName(path);
  // The app never shows Stripe's checkout — Apple's rule. In the app every wall is screen B
  // (src/rbapp/Paywall.tsx): Apple's own purchase, no website, no card form (owner, 2026-10-22).

  if (CRAWLING) return <>{children}</>;

  if (access.state === "loading") {
    return (
      <TickerShell crumbs={[{ label: "Home", href: "/" }]}>
        <div className="rbacct" />
      </TickerShell>
    );
  }

  if (access.state === "open") {
    const line = trialLine(access.daysLeft);
    return (
      <>
        {line && (
          <p className="rbacct-trialstrip">
            {line} · <a href={inApp ? "/m/you" : "/account"}>{inApp ? "Your account" : "Pick a plan"}</a>
          </p>
        )}
        {children}
      </>
    );
  }

  const wall =
    inApp ? <Paywall />
    : access.state === "expired" ? <PlanWall />
    : <GateCard tool={tool} />;
  return besideWall ? <>{wall}{besideWall}</> : wall;
}

/** Free month over, no plan: the app's signage (MemberPitch — the 200+ barns,
 *  the barns selling now, everything a Member gets) around the plan card,
 *  monthly or yearly, one tap to Stripe's checkout. The picker is the account
 *  page's own (SiteBilling), so the two can never offer different things. */
function PlanWall() {
  return (
    <TickerShell crumbs={[{ label: "Home", href: "/" }, { label: "Subscribe" }]}>
      <div className="rbacct">
        <MemberPitch>
          <div className="rbacct-card" data-wall="plans">
            <p className="rbacct-eyebrow">Your free month has ended</p>
            <h1 className="rbacct-title">Subscribe to keep going</h1>
            <SiteBilling onWall />
            <p className="rbacct-fine">The homepage and Boxed Beef stay free. <a href={siteHomeHref()}>Back to today&rsquo;s prices</a></p>
          </div>
        </MemberPitch>
      </div>
    </TickerShell>
  );
}

/** Signed out: the membership page itself, naming the tool (owner, 2026-10-25:
 *  "one page for all"). The form, the price and the pitch are the same ones a
 *  visitor finds from the masthead's Join, so there is one way in. */
function GateCard({ tool }: { tool: string }) {
  return <MembershipPage tool={tool} />;
}
